import { FC, useEffect, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {ProductThumb} from '../../../components/common/ProductThumb'
import { Link } from 'react-router-dom'
import { IProductModel, PRODUCT_ACTIONS } from '../cors/_models'
import { useIntl } from 'react-intl'
import CountdownTimer from '../components/CountdownTimer'
import { WinningStar } from '../../offers/shared'
import { decodeHtmlEntities } from '../../common/decodeHtml'
import { displayQuantity } from '../../common/stockScope'

type IProps = {
  product: IProductModel
  onChangeQty: (value: string, prdID: number) => void
  onAction: (action: PRODUCT_ACTIONS, productID: number) => Promise<any>
  /** The signed-in supplier runs a partner shop: the quantity is its shelf, and there is no FC column. */
  shopUser?: boolean
}
let userUpdateQtyTimeout: NodeJS.Timeout

const ProductTableRow: FC<IProps> = ({ product, onChangeQty, onAction, shopUser = false }) => {
  const { formatMessage } = useIntl()
  // The shelf for a shop account (stock_scope "shop"), national supplier stock otherwise.
  const currentQty = displayQuantity(product)
  const [qtyInput, setQtyInput] = useState<number | string>(currentQty)
  // After a save (or a refused one) the list reloads; show the number the server now holds.
  useEffect(() => {
    setQtyInput(currentQty)
  }, [currentQty])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // A package is assembled from its components, so its quantity is derived
  // (floor(component stock / per-pack size), minimised across the recipe) and
  // recomputed on every component movement. Offering an editable box here would
  // invite a number the server refuses and the next recompute would overwrite.
  const isPackage = Number(product.is_offer) === 1

  const handleQuantityChange = (e: React.ChangeEvent<any>) => {
    if (isPackage) {
      return
    }

    const { value } = e.target
    const n = Number(value)
    // Only a non-negative integer may be pushed to the API — a negative would
    // fail the offer-sync non-negative guard server-side and drift the offer.
    if (value !== '' && Number.isInteger(n) && n >= 0) {
      setQtyInput(value)

      clearTimeout(userUpdateQtyTimeout)

      userUpdateQtyTimeout = setTimeout(() => {
        onChangeQty(value, product.product_id)
      }, 1000)
    }
  }

  const handleAction = async (action: PRODUCT_ACTIONS) => {
    setIsLoading(true)
    await onAction(action, product.product_id)
    setIsLoading(false)
  }

  const specialDiscount = (oldPrice: string, newPrice: string) => {
    let convertedOldPrice = (+oldPrice).toFixed(2)
    return (
      <>
        {newPrice ? (
          <>
            <del>{convertedOldPrice}</del>
            <br />
            <span className="text-danger">{newPrice}</span>
          </>
        ) : (
          <>{convertedOldPrice}</>
        )}
      </>
    )
  }

  return (
    <tr className='hsb-pl-row'>
      <th scope='row'>
        <ProductThumb src={product.image} className='hsb-pl-image' />
      </th>
      <td className='hsb-pl-product-name'>
        {/* Multi-supplier (T2): the price/qty in this row are the supplier's
            OWN offer values. Surface that + winning status. The star renders
            once the list returns is_winning_offer (BE-A); badges use the
            already-returned has_offer / is_catalog_owner flags. */}
        <div className="d-flex align-items-center gap-2">
          {product.has_offer === 1 && product.is_winning_offer !== undefined ? (
            <WinningStar winning={product.is_winning_offer === 1} />
          ) : null}
          <span>{decodeHtmlEntities(product.name)}</span>
        </div>
        {(product.has_offer === 1 || product.is_catalog_owner === 1) ? (
          <div className="d-flex flex-wrap gap-1 mt-1">
            {product.has_offer === 1 ? (
              <span className="badge badge-light-primary fs-8">
                {formatMessage({ id: 'product.badge.your_offer' })}
              </span>
            ) : null}
            {product.is_catalog_owner === 1 ? (
              <span className="badge badge-light-info fs-8">
                {formatMessage({ id: 'product.badge.catalog_owner' })}
              </span>
            ) : null}
            {product.has_offer === 1 && product.offer_is_active === 0 ? (
              <span className="badge badge-light-warning fs-8">
                {formatMessage({ id: 'product.badge.offer_paused' })}
              </span>
            ) : null}
          </div>
        ) : null}
      </td>

      <td className='hsb-pl-code'>
        {product.barcode}
      </td>

      <td className='hsb-pl-code'>
        {product.model}
      </td>

      <td>
        {isPackage ? (
          // The box has to say WHY it cannot be edited, or a read-only field
          // just looks broken. react-bootstrap's tooltip (the same one the
          // offers summary uses) rather than the native `title`: title is easy
          // to miss, and browsers are inconsistent about firing it on form
          // controls the user cannot focus. The wrapping span is what the
          // trigger listens on, so hover works even though the input itself is
          // inert.
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`pkg-qty-${product.product_id}`}>
                {formatMessage({ id: 'package_quantity_is_derived' })}
              </Tooltip>
            }
          >
            <span className="d-inline-block" style={{ cursor: 'not-allowed' }}>
              <input
                type="number"
                className='hsb-pl-qty-input'
                readOnly
                style={{
                  '--qty-len': String(qtyInput).length,
                  pointerEvents: 'none',
                  backgroundColor: '#f1f1f2',
                  borderColor: '#e4e6ef',
                  color: '#7e8299',
                } as React.CSSProperties}
                value={qtyInput}
                min="0"
              />
            </span>
          </OverlayTrigger>
        ) : (
          <input
            onChange={handleQuantityChange}
            type="number"
            className='hsb-pl-qty-input'
            style={{'--qty-len': String(qtyInput).length} as React.CSSProperties}
            onWheel={(e: any) => e.target.blur()}
            value={qtyInput}
            min="0"
          />
        )}
      </td>
      {!shopUser && <td><span className='hsb-pl-qty-badge'>{product.warehouse_quantity}</span></td>}

      <td className='hsb-pl-price'>
        {specialDiscount(product.price, product.special_sale)}
        {product.special_sale && product.special_end_time && (
          <div style={{ marginTop: '4px' }}>
            <CountdownTimer 
              endDateTime={product.special_end_time}
              onExpire={() => {
                console.log('Special offer expired for product:', product.product_id);
              }}
            />
          </div>
        )}
      </td>

      <td>{product.manufacturer_name || '—'}</td>
      <td className='hsb-pl-status'>
        {`${product.product_status} - ${
          (product as any).stock_status
            ? (product as any).stock_status
            : (currentQty > 0
                ? formatMessage({ id: 'in_stock' })
                : formatMessage({ id: 'out_of_stock' }))
        }`}
      </td>

      <td className='hsb-pl-performance'>
        {formatMessage({ id: 'sold_qty' })}: {product.sold_units}
        <br />
        {formatMessage({ id: 'view' })}: {product.views}
      </td>

      <td className='hsb-pl-row-actions'>
        {isLoading ? (
          <span className="align-middle spinner-border spinner-border-sm"></span>
        ) : (
          <>
            <div className='hsb-pl-row-action-stack'>
              {/* Any supplier with an active offer on the product can
                  file an edit request via the catalog form. The Lumen
                  endpoint validates eligibility, then the change goes
                  into pp_edit_product_request for catalog-team review.
                  See ProductController::supplierCanEditProduct. */}
              <Link
                className='hsb-pl-row-button hsb-pl-edit'
                to={`/products/product-edit/${product.product_id}`}
              >
                {formatMessage({ id: 'edit' })}
              </Link>
              {/* Not for a package: "out of stock" zeroes the offer's quantity, and a
                  package's quantity is derived from its components — the next
                  component movement would simply put it back. The way to take a
                  package off sale is to take its component off sale. */}
              {!isPackage && product.status_id && product.status_id !== 3 && currentQty > 0 && (
                <button
                  className='hsb-pl-row-button hsb-pl-out-of-stock'
                  onClick={() => handleAction(PRODUCT_ACTIONS.OUT_OF_STOCK)}
                >
                  {formatMessage({ id: 'out_of_stock' })}
                </button>
              )}
              {typeof product.view !== 'boolean' && (
                <a
                  target="_blank"
                  className='hsb-pl-row-button hsb-pl-view'
                  href={product.view}
                >
                  {formatMessage({ id: 'view' })}
                </a>
              )}
            </div>
          </>
        )}
      </td>
    </tr>
  )
}

export default ProductTableRow
